// ClientListView.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Button, TextField, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Popover, CssBaseline, useMediaQuery, List, ListItem, ListItemText, Grid, Accordion, AccordionSummary, AccordionDetails, Tooltip, Tabs, Tab, Checkbox, FormControlLabel, Badge, Chip, FormControl, InputLabel, Select, MenuItem, InputAdornment } from '@mui/material';
import { ConfigProvider } from 'antd';
import {
  Building2, 
  UserPlus, 
  Edit, 
  Trash2, 
  MapPin, 
  Search, 
  X, 
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Mail,
  Phone,
  Star,
  AlertCircle,
  Copy,
  CheckCircle2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Building,
  Users,
  Link,
  Link2,
  Globe,
  Map,
  CheckCircle
} from 'lucide-react';
import { useReactTable, getCoreRowModel, flexRender, getSortedRowModel } from '@tanstack/react-table';
import api from "../../../authentication/axiosInstance";
import debounce from 'lodash/debounce';
import SiteContactsManager from '../../../components/SiteContactsManager';
import styles from './ClientsListView.module.css';
import { Select as AntSelect, DatePicker, InputNumber } from 'antd';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import moment from 'moment';

const compareText = (a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });

export const sortClientsForDatabase = (clientList) =>
  [...clientList].sort((a, b) => {
    const nameCompare = compareText(a.client_name, b.client_name);
    if (nameCompare !== 0) return nameCompare;
    return (a.client_id || 0) - (b.client_id || 0);
  });

export const filterClientsForDatabase = (clientList, searchValue = '') => {
  const normalizedSearch = searchValue.toLowerCase();
  return sortClientsForDatabase(
    clientList.filter((client) =>
      (client.client_name?.toLowerCase() || '').includes(normalizedSearch) ||
      `${client.primary_contact?.first_name || ''} ${client.primary_contact?.last_name || ''}`.toLowerCase().includes(normalizedSearch) ||
      (client.primary_contact?.email?.toLowerCase() || '').includes(normalizedSearch)
    )
  );
};

export const getClientSiteCoverageValidationError = (value) => {
  if (value === '' || value === null || value === undefined) {
    return 'Site coverage must be a whole number between 0 and 100.';
  }
  const coverage = Number(value);
  if (!Number.isInteger(coverage) || coverage < 0 || coverage > 100) {
    return 'Site coverage must be a whole number between 0 and 100.';
  }
  return null;
};

const SITE_EDIT_NUMERIC_FIELDS = [
  'parking_lots_sqft',
  'walkway_sqft',
  'base_revenue',
  'monthly_rate',
  'additional_revenue_amount',
];

const SITE_EDIT_NULLABLE_FIELDS = [
  'winter_start_date',
  'winter_end_date',
  'contract_expiration_date',
  'additional_revenue_type',
  'assigned_territory_manager',
  'assigned_site_supervisor',
  'viaesys_site_id',
  'qbo_key_account_id',
  'dispatch_notes',
];

export const normalizeClientSiteEditDetail = (site) => ({
  ...site,
  site_name: site.site_name ?? '',
  address: site.address ?? '',
  latitude: site.latitude?.toString() ?? '',
  longitude: site.longitude?.toString() ?? '',
  region: site.region ?? '',
  parking_lots_sqft: site.parking_lots_sqft?.toString() ?? '',
  walkway_sqft: site.walkway_sqft?.toString() ?? '',
  site_coverage: site.site_coverage?.toString() ?? '',
  base_revenue: site.base_revenue?.toString() ?? '',
  winter_start_date: site.winter_start_date ?? '',
  winter_end_date: site.winter_end_date ?? '',
  contract_expiration_date: site.contract_expiration_date ?? '',
  monthly_rate: site.monthly_rate?.toString() ?? '',
  additional_revenue_type: site.additional_revenue_type ?? '',
  additional_revenue_amount: site.additional_revenue_amount?.toString() ?? '',
  assigned_territory_manager: site.assigned_territory_manager ?? '',
  assigned_site_supervisor: site.assigned_site_supervisor ?? '',
  viaesys_site_id: site.viaesys_site_id ?? '',
  qbo_key_account_id: site.qbo_key_account_id ?? '',
  weather_station_id: site.weather_station?.id ?? '',
  dispatch_notes: site.dispatch_notes ?? '',
});

const normalizedNullableValue = (value) => {
  if (value === '' || value === undefined || value === null) return null;
  return typeof value === 'string' ? value.trim() || null : value;
};

export const buildClientSiteEditPayload = (form, initialForm) => {
  const payload = {};
  const requiredTextFields = ['site_name', 'address', 'region', 'contract_status'];
  const normalizedCurrent = {};
  const normalizedInitial = {};

  requiredTextFields.forEach((field) => {
    const value = typeof form[field] === 'string' ? form[field].trim() : form[field];
    if (!value) throw new Error(`Please enter ${field.replace(/_/g, ' ')}.`);
    normalizedCurrent[field] = value;
    normalizedInitial[field] = typeof initialForm[field] === 'string'
      ? initialForm[field].trim()
      : initialForm[field];
  });

  if (form.latitude === '' || form.latitude === undefined || form.latitude === null ||
      form.longitude === '' || form.longitude === undefined || form.longitude === null) {
    throw new Error('Please select a valid address.');
  }
  const latitude = Number(form.latitude);
  const longitude = Number(form.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error('Please select a valid address.');
  }
  normalizedCurrent.latitude = latitude.toFixed(6);
  normalizedCurrent.longitude = longitude.toFixed(6);
  normalizedInitial.latitude = Number(initialForm.latitude).toFixed(6);
  normalizedInitial.longitude = Number(initialForm.longitude).toFixed(6);

  SITE_EDIT_NUMERIC_FIELDS.forEach((field) => {
    if (form[field] === '' || form[field] === undefined || form[field] === null) {
      throw new Error(`Please enter ${field.replace(/_/g, ' ')}.`);
    }
    const value = Number(form[field]);
    if (!Number.isFinite(value) || value < 0) {
      throw new Error(`${field.replace(/_/g, ' ')} must be a non-negative number.`);
    }
    normalizedCurrent[field] = value;
    normalizedInitial[field] = Number(initialForm[field]);
  });

  const coverageError = getClientSiteCoverageValidationError(form.site_coverage);
  if (coverageError) throw new Error(coverageError);
  normalizedCurrent.site_coverage = Number(form.site_coverage);
  normalizedInitial.site_coverage = Number(initialForm.site_coverage);

  SITE_EDIT_NULLABLE_FIELDS.forEach((field) => {
    normalizedCurrent[field] = normalizedNullableValue(form[field]);
    normalizedInitial[field] = normalizedNullableValue(initialForm[field]);
  });
  normalizedCurrent.weather_station_id = form.weather_station_id === '' || form.weather_station_id === null
    ? null
    : Number(form.weather_station_id);
  normalizedInitial.weather_station_id = initialForm.weather_station_id === '' || initialForm.weather_station_id === null
    ? null
    : Number(initialForm.weather_station_id);

  Object.keys(normalizedCurrent).forEach((field) => {
    if (normalizedCurrent[field] !== normalizedInitial[field]) {
      payload[field] = normalizedCurrent[field];
    }
  });

  if (payload.address) {
    payload.latitude = normalizedCurrent.latitude;
    payload.longitude = normalizedCurrent.longitude;
  }
  return payload;
};

export const getClientStatusPreviewMessage = (preview) => {
  const allocationCount = preview?.allocation_count ?? 0;
  const setupCount = preview?.setup_count ?? 0;
  const siteCount = preview?.transitioning_site_count ?? 0;
  const retainedCount = preview?.retained_shared_loader_staff_assignment_count ?? preview?.shared_loader_resources_retained ?? 0;
  const lockedCount = preview?.locked_allocation_count ?? 0;
  return `This status change affects ${siteCount} Confirmed site(s). Removed from editable/live planning: ${allocationCount} allocation(s) across ${setupCount} editable setup(s). Shared loader staff assignments left in place: ${retainedCount}. Allocations preserved in locked history: ${lockedCount}. Apply changes?`;
};

export const runClientSiteStatusChange = async ({
  apiClient,
  site,
  payload,
  confirmImpact = (preview) => window.confirm(getClientStatusPreviewMessage(preview)),
  onPreviewChanged = () => alert('Allocation impact changed. Review and confirm again.'),
}) => {
  const mutation = (previewHash) => apiClient.patch(`/sites/${site.site_id}/`, {
    ...payload,
    ...(previewHash ? { status_preview_hash: previewHash } : {}),
  });

  if (site.contract_status !== 'Confirmed' || payload.contract_status === undefined || payload.contract_status === 'Confirmed') {
    return mutation(null);
  }

  const previewResponse = await apiClient.post('/sites/status-deallocation-preview/', {
    site_ids: [site.site_id],
    target_status: payload.contract_status,
  });
  let preview = previewResponse.data;
  while (preview?.transitioning_site_count > 0) {
    if (!confirmImpact(preview)) return null;
    try {
      return await mutation(preview.preview_hash);
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.preview) {
        preview = error.response.data.preview;
        onPreviewChanged(preview);
        continue;
      }
      throw error;
    }
  }
  return mutation(null);
};

const isClientSiteAllocationPresent = (site) => Boolean(site && (
  site.parking_lots_sqft_allocated > 0 ||
  site.walkway_sqft_allocated > 0 ||
  site.assigned_walkway_crew_bool ||
  site.assigned_salting_route_bool
));

const clientContactName = (client) => {
  const contact = client.primary_contact;
  return contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : '';
};

const getClientContactFormData = (client) => ({
  first_name: client.contact_first_name || '',
  last_name: client.contact_last_name || '',
  phone: client.contact_phone || '',
  email: client.contact_email || '',
});

const hasAnyContactFormValue = (contactData) =>
  Object.values(contactData).some((value) => value.trim());

const hasCompleteContactForm = (contactData) =>
  Object.values(contactData).every((value) => value.trim());

const hasPrimaryContactChanges = (client) => {
  const primaryContact = client.primary_contact;
  if (!primaryContact) return false;

  const contactData = getClientContactFormData(client);
  return (
    contactData.first_name !== (primaryContact.first_name || '') ||
    contactData.last_name !== (primaryContact.last_name || '') ||
    contactData.phone !== (primaryContact.phone || '') ||
    contactData.email !== (primaryContact.email || '')
  );
};

const ClientListView = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deletePopoverId, setDeletePopoverId] = useState(null);
  const [selectedClientSites, setSelectedClientSites] = useState([]);
  const [isSitesModalOpen, setIsSitesModalOpen] = useState(false);
  const [sitesSearchText, setSitesSearchText] = useState('');
  const [clientContacts, setClientContacts] = useState([]);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState([{ id: 'clientName', desc: false }]);
  const [copiedField, setCopiedField] = useState(null);
  const [copiedTooltip, setCopiedTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const [contactsFilter, setContactsFilter] = useState('all');
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [primaryContactSubmittingId, setPrimaryContactSubmittingId] = useState(null);
  const [contactTabValue, setContactTabValue] = useState(0);
  const [clientSites, setClientSites] = useState([]);
  const [clientSitesLoading, setClientSitesLoading] = useState(false);
  const [siteContactAssignments, setSiteContactAssignments] = useState({});
  const [selectedContactForSites, setSelectedContactForSites] = useState(null);
  const [isSiteAssignmentModalOpen, setIsSiteAssignmentModalOpen] = useState(false);
  const [siteContactMatrix, setSiteContactMatrix] = useState({});
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [expandedViewTab, setExpandedViewTab] = useState(0);
  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
  const [siteForm, setSiteForm] = useState({ site_coverage: 0 });
  const [siteDialogMode, setSiteDialogMode] = useState('add');
  const [editingSite, setEditingSite] = useState(null);
  const [initialSiteForm, setInitialSiteForm] = useState(null);
  const [siteEditLoading, setSiteEditLoading] = useState(false);
  const [siteSubmitting, setSiteSubmitting] = useState(false);
  const [territoryManagers, setTerritoryManagers] = useState([]);
  const [territoryManagersLoaded, setTerritoryManagersLoaded] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [weatherStations, setWeatherStations] = useState([]);
  const [contactsByClientId, setContactsByClientId] = useState({});
  const [sitesByClientId, setSitesByClientId] = useState({});
  const [matrixByClientId, setMatrixByClientId] = useState({});
  const activeClientIdRef = useRef(null);
  const siteEditRequestIdRef = useRef(0);

  const isMobile = useMediaQuery('(max-width:768px)');

  const fetchClients = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/clients/management_list/');
      setClients(sortClientsForDatabase(response.data || []));
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchTerritoryManagers = async (rethrow = false) => {
    if (territoryManagersLoaded) return territoryManagers;

    try {
      const response = await api.get('/staff/territory_manager_staff/');
      const managers = Array.isArray(response.data) ? response.data : [];
      setTerritoryManagers(managers);
      setTerritoryManagersLoaded(true);
      return managers;
    } catch (error) {
      console.error('Failed to fetch Territory Managers:', error);
      if (rethrow) throw error;
      return [];
    }
  };

  const ensureSiteReferenceDataLoaded = async () => {
    await fetchTerritoryManagers();
  };

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSearch = useCallback((value) => {
    setSearchText(value);
  }, []);

  const debouncedSearch = useMemo(() => debounce(handleSearch, 300), [handleSearch]);

  useEffect(() => {
    setFilteredClients(filterClientsForDatabase(clients, searchText));
  }, [clients, searchText]);

  const showModal = (client = null) => {
    setIsEditMode(!!client);
    setEditingClient(client ? {
      ...client,
      contact_first_name: client.primary_contact?.first_name || '',
      contact_last_name: client.primary_contact?.last_name || '',
      contact_phone: client.primary_contact?.phone || '',
      contact_email: client.primary_contact?.email || '',
    } : {
      client_name: '',
      contact_first_name: '',
      contact_last_name: '',
      contact_phone: '',
      contact_email: '',
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (isEditMode) {
        const contactData = getClientContactFormData(editingClient);
        const shouldCreatePrimaryContact = !editingClient.primary_contact && hasAnyContactFormValue(contactData);

        if (shouldCreatePrimaryContact && !hasCompleteContactForm(contactData)) {
          alert('Please complete all primary contact fields before saving a new primary contact.');
          return;
        }

        await api.patch(`/clients/${editingClient.client_id}/`, {
          client_name: editingClient.client_name,
        });

        let updatedPrimaryContact = editingClient.primary_contact;
        let contactCountIncrement = 0;

        if (editingClient.primary_contact && hasPrimaryContactChanges(editingClient)) {
          const contactResponse = await api.patch(
            `/contacts/${editingClient.primary_contact.contact_id}/`,
            contactData
          );
          updatedPrimaryContact = {
            ...editingClient.primary_contact,
            ...contactResponse.data,
          };
          invalidateClientCache(editingClient.client_id, ['contacts', 'matrix']);
        } else if (shouldCreatePrimaryContact) {
          const contactResponse = await api.post(`/clients/${editingClient.client_id}/contacts/`, {
            ...contactData,
            notes: '',
            make_primary: true,
          });
          updatedPrimaryContact = contactResponse.data;
          contactCountIncrement = 1;
          invalidateClientCache(editingClient.client_id, ['contacts', 'matrix']);
        }

        setClients((prevClients) => sortClientsForDatabase(
          prevClients.map((client) =>
            client.client_id === editingClient.client_id
              ? {
                  ...client,
                  client_name: editingClient.client_name,
                  primary_contact: updatedPrimaryContact,
                  contact_count: (client.contact_count || 0) + contactCountIncrement,
                }
              : client
          )
        ));
      } else {
        const clientResponse = await api.post('/clients/', {
          client_name: editingClient.client_name,
        });
        const newClientId = clientResponse.data.client_id;
        if (
          editingClient.contact_first_name ||
          editingClient.contact_last_name ||
          editingClient.contact_phone ||
          editingClient.contact_email
        ) {
          await api.post(`/clients/${newClientId}/contacts/`, {
            first_name: editingClient.contact_first_name,
            last_name: editingClient.contact_last_name,
            phone: editingClient.contact_phone,
            email: editingClient.contact_email,
            notes: '',
            make_primary: true,
          });
        }
        await fetchClients();
      }
      handleCloseModal();
    } catch (error) {
      console.error('Failed to submit client:', error);
    }
  };

  const handleDelete = async (clientId) => {
    try {
      await api.delete(`/clients/${clientId}/`);
      setClients((prevClients) => prevClients.filter((client) => client.client_id !== clientId));
      setContactsByClientId((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
      setSitesByClientId((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
      setMatrixByClientId((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
    } catch (error) {
      console.error('Failed to delete client:', error);
    }
    setDeletePopoverId(null);
  };

  const invalidateClientCache = (clientId, cacheNames = ['contacts', 'sites', 'matrix']) => {
    if (cacheNames.includes('contacts')) {
      setContactsByClientId((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
      if (activeClientIdRef.current === clientId) {
        setClientContacts([]);
      }
    }
    if (cacheNames.includes('sites')) {
      setSitesByClientId((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
      if (activeClientIdRef.current === clientId) {
        setClientSites([]);
      }
    }
    if (cacheNames.includes('matrix')) {
      setMatrixByClientId((prev) => {
        const next = { ...prev };
        delete next[clientId];
        return next;
      });
      if (activeClientIdRef.current === clientId) {
        setSiteContactMatrix({});
        setSiteContactAssignments({});
      }
    }
  };

  const setVisibleExpandedClientData = (clientId) => {
    const cachedMatrix = matrixByClientId[clientId];
    setClientContacts(contactsByClientId[clientId] || cachedMatrix?.contacts || []);
    setClientSites(sitesByClientId[clientId] || cachedMatrix?.sites || []);
    setSiteContactMatrix(cachedMatrix?.matrix || {});
    setSiteContactAssignments({});
    setSelectedContactForSites(null);
  };

  const updateClientRow = (clientId, updater) => {
    setClients((prevClients) => sortClientsForDatabase(
      prevClients.map((client) => (
        client.client_id === clientId ? updater(client) : client
      ))
    ));
  };

  const updateContactsForClient = (clientId, updater) => {
    setContactsByClientId((prev) => {
      const currentContacts = prev[clientId] || [];
      return {
        ...prev,
        [clientId]: updater(currentContacts),
      };
    });
    if (activeClientIdRef.current === clientId || editingClient?.client_id === clientId) {
      setClientContacts((currentContacts) => updater(currentContacts));
    }
  };

  const appendOrReplaceContactForClient = (clientId, contact) => {
    updateContactsForClient(clientId, (currentContacts) => {
      const exists = currentContacts.some((existing) => existing.contact_id === contact.contact_id);
      if (exists) {
        return currentContacts.map((existing) => (
          existing.contact_id === contact.contact_id ? { ...existing, ...contact } : existing
        ));
      }
      return [...currentContacts, contact];
    });
  };

  const applyPrimaryContactLocally = (clientId, primaryContact) => {
    updateClientRow(clientId, (client) => ({
      ...client,
      primary_contact: primaryContact,
    }));
    updateContactsForClient(clientId, (currentContacts) => (
      currentContacts.map((contact) => ({
        ...contact,
        is_primary: contact.contact_id === primaryContact.contact_id,
      }))
    ));
  };

  const fetchClientSites = async (clientId, forContactsModal = false, force = false) => {
    try {
      setClientSitesLoading(forContactsModal);
      let sites = sitesByClientId[clientId];
      if (!sites || force) {
        const response = await api.get(`/clients/${clientId}/management_sites/`);
        sites = response.data || [];
        setSitesByClientId((prev) => ({
          ...prev,
          [clientId]: sites,
        }));
      }
      
      if (forContactsModal) {
        if (activeClientIdRef.current === clientId || editingClient?.client_id === clientId) {
          setClientSites(sites);
        }
        setClientSitesLoading(false);
      } else {
        setSelectedClientSites(sites);
        setIsSitesModalOpen(true);
        setSitesSearchText('');
      }
    } catch (error) {
      console.error('Failed to fetch client sites:', error);
      setClientSitesLoading(false);
    }
  };

  const handleCloseSitesModal = () => {
    setIsSitesModalOpen(false);
    setSelectedClientSites([]);
    setSitesSearchText('');
  };

  const handleSearchInputChange = (e) => {
    setSearchInputValue(e.target.value);
  };

  const debouncedSetSitesSearch = useMemo(
    () => debounce((value) => {
      setSitesSearchText(value);
    }, 300),
    []
  );

  const filteredSites = useMemo(() => {
    const searchValue = searchInputValue.toLowerCase();
    if (!searchValue) return selectedClientSites;
    
    return selectedClientSites.filter(
      site => site.site_name.toLowerCase().includes(searchValue) || 
             site.region.toLowerCase().includes(searchValue)
    );
  }, [searchInputValue, selectedClientSites]);

  const fetchClientContacts = async (clientId, force = false) => {
    try {
      let contacts = contactsByClientId[clientId];
      if (!contacts || force) {
        const response = await api.get(`/clients/${clientId}/contacts/`);
        contacts = response.data || [];
        setContactsByClientId((prev) => ({
          ...prev,
          [clientId]: contacts,
        }));
      }
      if (activeClientIdRef.current === clientId || editingClient?.client_id === clientId) {
        setClientContacts(contacts);
      }
      return contacts;
    } catch (error) {
      console.error('Failed to fetch client contacts:', error);
      return [];
    }
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    if (isContactSubmitting) return;
    const currentContact = editingContact || {};
    const clientId = editingClient.client_id;
    setIsContactSubmitting(true);
    try {
      if (currentContact.contact_id) {
        const response = await api.put(`/contacts/${currentContact.contact_id}/`, currentContact);
        updateClientRow(editingClient.client_id, (client) => (
          client.primary_contact?.contact_id === currentContact.contact_id
            ? { ...client, primary_contact: { ...client.primary_contact, ...response.data } }
            : client
        ));
        appendOrReplaceContactForClient(clientId, response.data);
      } else {
        const response = await api.post(`/clients/${clientId}/contacts/`, currentContact);
        appendOrReplaceContactForClient(clientId, response.data);
        updateClientRow(clientId, (client) => ({
          ...client,
          contact_count: (client.contact_count || 0) + 1,
          primary_contact: response.data?.is_primary ? response.data : client.primary_contact,
        }));
      }
      invalidateClientCache(clientId, ['matrix']);
      await fetchClientContacts(clientId, true);
      setEditingContact(null);
    } catch (error) {
      console.error('Failed to submit contact:', error);
    } finally {
      setIsContactSubmitting(false);
    }
  };

  const handleSetPrimaryContact = async (clientId, contactId) => {
    if (primaryContactSubmittingId) return;
    setPrimaryContactSubmittingId(contactId);
    try {
      const response = await api.put(`/clients/${clientId}/primary-contact/${contactId}/`);
      applyPrimaryContactLocally(clientId, response.data);
      invalidateClientCache(clientId, ['matrix']);
      await fetchClientContacts(clientId, true);
    } catch (error) {
      console.error('Failed to set primary contact:', error);
    } finally {
      setPrimaryContactSubmittingId(null);
    }
  };

  const blankContact = () => ({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    notes: ''
  });

  const showContactModal = (contact = null) => {
    setEditingContact(contact || blankContact());
    setIsContactModalOpen(true);
  };

  const handleCopyToClipboard = async (text, fieldName, event) => {
    try {
      await navigator.clipboard.writeText(text);
      const rect = event.currentTarget.getBoundingClientRect();
      setCopiedTooltip({
        show: true,
        text: 'Copied!',
        x: rect.left + rect.width / 2,
        y: rect.top - 20
      });
      setTimeout(() => setCopiedTooltip({ show: false, text: '', x: 0, y: 0 }), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const filteredContacts = useMemo(() => {
    const searchValue = searchInputValue.toLowerCase();
    let filtered = clientContacts;
    
    // Apply search filter
    if (searchValue) {
      filtered = filtered.filter(contact => 
        contact.first_name?.toLowerCase().includes(searchValue) ||
        contact.last_name?.toLowerCase().includes(searchValue) ||
        contact.email?.toLowerCase().includes(searchValue) ||
        contact.phone?.toLowerCase().includes(searchValue) ||
        contact.notes?.toLowerCase().includes(searchValue)
      );
    }
    
    // Apply type filter
    if (contactsFilter === 'primary') {
      filtered = filtered.filter(contact => contact.is_primary);
    }
    
    return filtered;
  }, [clientContacts, searchInputValue, contactsFilter]);

  const columns = useMemo(
    () => [
      {
        id: 'clientName',
        header: ({ column }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              '&:hover': { color: '#034832' }
            }}
            onClick={column.getToggleSortingHandler()}
          >
            Client Name
            <span style={{ opacity: column.getIsSorted() ? 1 : 0.5 }}>
              {column.getIsSorted() === 'asc' ? <ArrowUp size={14} /> : 
               column.getIsSorted() === 'desc' ? <ArrowDown size={14} /> : 
               <ArrowUpDown size={14} />}
            </span>
          </Box>
        ),
        accessorKey: 'client_name',
        sortingFn: (rowA, rowB) => {
          const nameCompare = compareText(rowA.original.client_name, rowB.original.client_name);
          if (nameCompare !== 0) return nameCompare;
          return (rowA.original.client_id || 0) - (rowB.original.client_id || 0);
        },
        cell: ({ row }) => (
          <div className={styles.clientNameCell}>
            {row.original.client_name}
            {expandedClientId === row.original.client_id && (
              <ChevronUp size={16} className={styles.expandIcon} />
            )}
            {expandedClientId !== row.original.client_id && (
              <ChevronDown size={16} className={styles.expandIcon} />
            )}
          </div>
        ),
      },
      {
        id: 'contact',
        header: ({ column }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              '&:hover': { color: '#034832' }
            }}
            onClick={column.getToggleSortingHandler()}
          >
            Contact Information
            <span style={{ opacity: column.getIsSorted() ? 1 : 0.5 }}>
              {column.getIsSorted() === 'asc' ? <ArrowUp size={14} /> : 
               column.getIsSorted() === 'desc' ? <ArrowDown size={14} /> : 
               <ArrowUpDown size={14} />}
            </span>
          </Box>
        ),
        accessorFn: (row) => `${row.primary_contact?.first_name || ''} ${row.primary_contact?.last_name || ''}`,
        cell: ({ row }) => (
          <div className={styles.contactCell}>
            {row.original.primary_contact ? (
              <>
                <div className={styles.contactName}>
                  {row.original.primary_contact.first_name} {row.original.primary_contact.last_name}
                  <span className={styles.primaryContact}>Primary</span>
                </div>
                <div 
                  className={styles.contactInfo}
                  onClick={(e) => handleCopyToClipboard(row.original.primary_contact.email, 'email', e)}
                >
                  <Mail size={14} />
                  {row.original.primary_contact.email}
                </div>
                <div 
                  className={styles.contactInfo}
                  onClick={(e) => handleCopyToClipboard(row.original.primary_contact.phone, 'phone', e)}
                >
                  <Phone size={14} />
                  {row.original.primary_contact.phone}
                </div>
              </>
            ) : (
              <div className={styles.noContact}>No primary contact</div>
            )}
          </div>
        ),
      },
      {
        id: 'metadata',
        header: () => (
          <Box sx={{ 
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            Info
          </Box>
        ),
        cell: ({ row }) => {
          const contactsCount = row.original.contact_count || 0;
          const sitesCount = row.original.site_count || 0;
          
          return (
            <div className={styles.metadataCell}>
              <span className={styles.metadataBadge}>
                <Users size={14} />
                {contactsCount} contact{contactsCount !== 1 ? 's' : ''}
              </span>
              <span className={styles.metadataBadge}>
                <Building size={14} />
                {sitesCount} site{sitesCount !== 1 ? 's' : ''}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: () => (
          <Box sx={{ 
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            Actions
          </Box>
        ),
        cell: ({ row }) => (
          <Box className={styles.actionsCell}>
            <Tooltip title="Delete client" arrow>
              <IconButton 
                className={`${styles.actionButton} ${styles.danger}`}
                onClick={(event) => {
                  event.stopPropagation(); // Prevent row expansion when clicking delete
                  setEditingClient(row.original);
                  setDeletePopoverId(event.currentTarget);
                }}
              >
                <Trash2 size={18} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [expandedClientId]
  );

  const table = useReactTable({
    columns,
    data: filteredClients,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  const renderMobileView = () => (
    <Box>
      {filteredClients.map((client) => (
        <Paper key={client.client_id} elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1">{client.client_name}</Typography>
          <Typography variant="body2">{clientContactName(client) || 'No primary contact'}</Typography>
          <Typography variant="body2">{client.primary_contact?.phone || ''}</Typography>
          <Typography variant="body2">{client.primary_contact?.email || ''}</Typography>
          <Box sx={{ mt: 1 }}>
            <IconButton onClick={() => showModal(client)} color="primary" size="small">
              <Edit />
            </IconButton>
            <IconButton onClick={(event) => {
              setEditingClient(client);
              setDeletePopoverId(event.currentTarget);
            }} color="error" size="small">
              <Trash2 />
            </IconButton>
          </Box>
        </Paper>
      ))}
    </Box>
  );

  // Function to fetch site-contact assignments for a specific contact
  const fetchSiteContactAssignments = async (contactId) => {
    if (!contactId) return;
    
    try {
      const clientId = expandedClientId || editingClient?.client_id;
      if (!clientId) return;
      const matrixData = await fetchSiteContactMatrix(clientId);
      const assignments = {};
      matrixData.sites.forEach((site) => {
        assignments[site.site_id] = Boolean(matrixData.matrix[site.site_id]?.[contactId]);
      });
      
      setSiteContactAssignments(assignments);
    } catch (error) {
      console.error('Failed to fetch site contact assignments:', error);
    }
  };

  // New function to fetch all site contact assignments for the matrix view
  const fetchSiteContactMatrix = async (clientId = expandedClientId || editingClient?.client_id, force = false) => {
    if (!clientId) return { sites: [], contacts: [], matrix: {} };
    
    try {
      const cached = matrixByClientId[clientId];
      if (cached && !force) {
        if (activeClientIdRef.current === clientId || editingClient?.client_id === clientId) {
          setClientSites(cached.sites);
          setClientContacts(cached.contacts);
          setSiteContactMatrix(cached.matrix);
        }
        return cached;
      }

      const response = await api.get(`/clients/${clientId}/site_contact_matrix/`);
      const { sites = [], contacts = [], assignments = {} } = response.data || {};
      const matrix = {};

      sites.forEach((site) => {
        matrix[site.site_id] = {};
        contacts.forEach((contact) => {
          matrix[site.site_id][contact.contact_id] = (assignments[String(site.site_id)] || []).includes(contact.contact_id);
        });
      });

      const matrixData = { sites, contacts, matrix };

      if (activeClientIdRef.current === clientId || editingClient?.client_id === clientId) {
        setClientSites(sites);
        setClientContacts(contacts);
        setSiteContactMatrix(matrix);
      }
      setSitesByClientId((prev) => ({ ...prev, [clientId]: sites }));
      setContactsByClientId((prev) => ({ ...prev, [clientId]: contacts }));
      setMatrixByClientId((prev) => ({ ...prev, [clientId]: matrixData }));
      return matrixData;
    } catch (error) {
      console.error('Failed to fetch site contact matrix:', error);
      return { sites: [], contacts: [], matrix: {} };
    }
  };

  // Update contactTabChange to fetch matrix data when switching tabs
  const handleContactTabChange = (event, newValue) => {
    setContactTabValue(newValue);

    // Expanded client rows are loaded by the effect below. The contacts modal
    // is not tied to expandedViewTab, so it still needs an explicit fetch.
    if (newValue === 1 && isContactModalOpen) {
      fetchSiteContactMatrix(editingClient?.client_id);
    }
  };

  useEffect(() => {
    if (!expandedClientId || expandedViewTab !== 1 || contactTabValue !== 1) return;
    if (matrixByClientId[expandedClientId]) return;

    fetchSiteContactMatrix(expandedClientId);
  }, [expandedClientId, expandedViewTab, contactTabValue, matrixByClientId]);

  // Update the toggleSiteAssignment function to also update matrix data
  const toggleSiteAssignment = async (siteId, contactId, isAssigned) => {
    try {
      let contactIds = Object.entries(siteContactMatrix[siteId] || {})
        .filter(([, assigned]) => assigned)
        .map(([id]) => Number(id));
      
      if (isAssigned) {
        // Add this contact
        if (!contactIds.includes(contactId)) {
          contactIds.push(contactId);
        }
      } else {
        // Remove this contact
        contactIds = contactIds.filter(id => id !== contactId);
      }
      
      // Update site contacts
      await api.post(`/sites/${siteId}/contacts/`, {
        contact_ids: contactIds
      });
      
      // Update local state
      setSiteContactAssignments(prev => ({
        ...prev,
        [siteId]: isAssigned
      }));
      
      // Update matrix state
      setSiteContactMatrix(prev => ({
        ...prev,
        [siteId]: {
          ...prev[siteId],
          [contactId]: isAssigned
        }
      }));

      const clientId = expandedClientId || editingClient?.client_id;
      if (clientId) {
        setMatrixByClientId((prev) => {
          const cached = prev[clientId];
          if (!cached) return prev;
          return {
            ...prev,
            [clientId]: {
              ...cached,
              matrix: {
                ...cached.matrix,
                [siteId]: {
                  ...cached.matrix[siteId],
                  [contactId]: isAssigned,
                },
              },
            },
          };
        });
      }
      
    } catch (error) {
      console.error('Failed to update site contact assignment:', error);
    }
  };

  const handleContactModalOpen = (client) => {
    activeClientIdRef.current = client.client_id;
    setEditingClient(client);
    setVisibleExpandedClientData(client.client_id);
    fetchClientContacts(client.client_id);
    fetchClientSites(client.client_id, true);
    setIsContactModalOpen(true);
  };

  const handleOpenSiteAssignmentModal = (contact) => {
    setSelectedContactForSites(contact);
    fetchSiteContactAssignments(contact.contact_id);
    setIsSiteAssignmentModalOpen(true);
  };

  const handleCloseSiteAssignmentModal = () => {
    setIsSiteAssignmentModalOpen(false);
    setSelectedContactForSites(null);
  };

  // Toggle expanded state for a client row
  const toggleExpand = (clientId, event) => {
    // Prevent triggering when clicking action buttons
    if (event.target.closest(`.${styles.actionButton}`)) return;
    
    if (expandedClientId === clientId) {
      activeClientIdRef.current = null;
      setExpandedClientId(null);
    } else {
      activeClientIdRef.current = clientId;
      setExpandedClientId(clientId);
      setExpandedViewTab(0);
      setContactTabValue(0);
      setVisibleExpandedClientData(clientId);
      // Fetch necessary data when expanding
      const client = clients.find(c => c.client_id === clientId);
      if (client) {
        fetchClientContacts(clientId);
        fetchClientSites(clientId, true);
      }
    }
  };

  const extractStreetAddress = (fullAddress) => {
    const parts = fullAddress.split(',');
    return parts[0].trim();
  };

  const handleAddressSelect = async (selectedAddress) => {
    const address = siteDialogMode === 'edit'
      ? selectedAddress
      : extractStreetAddress(selectedAddress);
    setSiteForm(prev => ({
      ...prev,
      address,
      ...(siteDialogMode === 'edit' ? { latitude: '', longitude: '' } : {}),
    }));
    try {
      const geocode = await geocodeByAddress(selectedAddress);
      const { lat, lng } = await getLatLng(geocode[0]);
      setSiteForm(prev => ({
        ...prev, 
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6)
      }));
    } catch (error) {
      console.error('Error', error);
    }
  };

  const validateNumber = (value) => {
    if (value === '' || value === undefined) {
      return '';
    }
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) {
      return '';
    }
    return value;
  };

  const handleOpenSiteModal = async (client) => {
    setSiteDialogMode('add');
    setEditingClient(client);
    setEditingSite(null);
    setInitialSiteForm(null);
    setSiteForm({ site_coverage: 0 });
    await ensureSiteReferenceDataLoaded();
    setIsSiteModalOpen(true);
  };

  const handleCloseSiteModal = () => {
    if (siteSubmitting) return;
    siteEditRequestIdRef.current += 1;
    setIsSiteModalOpen(false);
    setSiteDialogMode('add');
    setEditingSite(null);
    setInitialSiteForm(null);
    setSiteEditLoading(false);
    setSiteForm({ site_coverage: 0 });
  };

  const handleOpenEditSiteModal = async (client, compactSite) => {
    const requestId = siteEditRequestIdRef.current + 1;
    siteEditRequestIdRef.current = requestId;
    setSiteDialogMode('edit');
    setEditingClient(client);
    setEditingSite(null);
    setInitialSiteForm(null);
    setSiteForm({});
    setSiteEditLoading(true);
    setIsSiteModalOpen(true);

    try {
      const [siteResponse, , staffResponse, weatherResponse] = await Promise.all([
        api.get(`/sites/${compactSite.site_id}/`),
        fetchTerritoryManagers(true),
        api.get('/staff/'),
        api.get('/weather-stations/'),
      ]);
      if (siteEditRequestIdRef.current !== requestId) return;

      const detail = siteResponse.data;
      const normalizedForm = normalizeClientSiteEditDetail(detail);
      setStaffList(Array.isArray(staffResponse.data) ? staffResponse.data : []);
      setWeatherStations(Array.isArray(weatherResponse.data) ? weatherResponse.data : []);
      setEditingSite(detail);
      setInitialSiteForm(normalizedForm);
      setSiteForm(normalizedForm);
    } catch (error) {
      if (siteEditRequestIdRef.current !== requestId) return;
      console.error('Failed to load site for editing:', error);
      alert('Failed to load the site. Please try again.');
      setIsSiteModalOpen(false);
      setSiteDialogMode('add');
    } finally {
      if (siteEditRequestIdRef.current === requestId) {
        setSiteEditLoading(false);
      }
    }
  };

  const handleSiteAddressChange = (value) => {
    setSiteForm((previous) => ({
      ...previous,
      address: value,
      ...(siteDialogMode === 'edit' ? { latitude: '', longitude: '' } : {}),
    }));
  };

  const handleEditSiteSubmit = async (event) => {
    event.preventDefault();
    if (!editingSite || !initialSiteForm || siteSubmitting) return;

    let payload;
    try {
      payload = buildClientSiteEditPayload(siteForm, initialSiteForm);
    } catch (error) {
      alert(error.message);
      return;
    }
    if (Object.keys(payload).length === 0) {
      alert('No site changes to save.');
      return;
    }

    setSiteSubmitting(true);
    let response;
    try {
      response = await runClientSiteStatusChange({
        apiClient: api,
        site: editingSite,
        payload,
      });
    } catch (error) {
      console.error('Failed to update site:', error);
      alert('Failed to update site. Please check your input and try again.');
      setSiteSubmitting(false);
      return;
    }

    if (!response) {
      setSiteSubmitting(false);
      return;
    }

    const clientId = editingClient.client_id;
    let refreshFailed = false;
    try {
      const refreshedSitesResponse = await api.get(`/clients/${clientId}/management_sites/`);
      const refreshedSites = Array.isArray(refreshedSitesResponse.data) ? refreshedSitesResponse.data : [];
      setSitesByClientId((previous) => ({ ...previous, [clientId]: refreshedSites }));
      if (activeClientIdRef.current === clientId) {
        setClientSites(refreshedSites);
      }
    } catch (error) {
      refreshFailed = true;
      console.error('Site updated, but failed to refresh the client site list:', error);
      const savedSite = response.data?.site_id === editingSite.site_id
        ? response.data
        : { ...editingSite, ...payload };
      const replaceSavedSite = (sites) => sites.map((site) => (
        site.site_id === editingSite.site_id ? { ...site, ...savedSite } : site
      ));
      setSitesByClientId((previous) => ({
        ...previous,
        [clientId]: replaceSavedSite(previous[clientId] || []),
      }));
      if (activeClientIdRef.current === clientId) {
        setClientSites((previous) => replaceSavedSite(previous));
      }
    }

    invalidateClientCache(clientId, ['matrix']);
    siteEditRequestIdRef.current += 1;
    setIsSiteModalOpen(false);
    setSiteDialogMode('add');
    setEditingSite(null);
    setInitialSiteForm(null);
    setSiteForm({ site_coverage: 0 });
    setSiteSubmitting(false);
    alert(refreshFailed
      ? 'Site updated successfully. The latest site list could not be reloaded, but your saved changes are shown.'
      : 'Site updated successfully');
  };

  const handleSiteSubmit = async (event) => {
    event.preventDefault();

    const rawSiteCoverage = siteForm.site_coverage;
    const siteCoverage = Number(rawSiteCoverage);
    const siteCoverageError = getClientSiteCoverageValidationError(rawSiteCoverage);
    if (siteCoverageError) {
      alert(siteCoverageError);
      return;
    }

    try {
      const formattedValues = {
        ...siteForm,
        client: editingClient.client_id,
        client_name: editingClient.client_name,
        parking_lots_sqft: parseFloat(siteForm.parking_lots_sqft || 0),
        walkway_sqft: parseFloat(siteForm.walkway_sqft || 0),
        site_coverage: siteCoverage,
        latitude: parseFloat(parseFloat(siteForm.latitude || 0).toFixed(6)),
        longitude: parseFloat(parseFloat(siteForm.longitude || 0).toFixed(6)),
        winter_start_date: siteForm.winter_start_date ? moment(siteForm.winter_start_date).format('YYYY-MM-DD') : null,
        winter_end_date: siteForm.winter_end_date ? moment(siteForm.winter_end_date).format('YYYY-MM-DD') : null,
        contract_expiration_date: siteForm.contract_expiration_date ? moment(siteForm.contract_expiration_date).format('YYYY-MM-DD') : null,
        base_revenue: parseFloat(siteForm.base_revenue || 0),
        monthly_rate: parseFloat(siteForm.monthly_rate || 0),
        additional_revenue_amount: parseFloat(siteForm.additional_revenue_amount || 0),
      };

      await api.post('/sites/', formattedValues);
      alert('Site added successfully');
      setIsSiteModalOpen(false);
      setSiteForm({ site_coverage: 0 });
      invalidateClientCache(editingClient.client_id, ['sites', 'matrix']);
      updateClientRow(editingClient.client_id, (client) => ({
        ...client,
        site_count: (client.site_count || 0) + 1,
      }));
      fetchClientSites(editingClient.client_id, true, true);
    } catch (error) {
      console.error('Failed to add site:', error);
      alert('Failed to add site. Please check your input and try again.');
    }
  };

  return (
    <ConfigProvider theme={{ token: { colorLinkHover: "#034832", colorPrimary: "#034832" } }}>
      <CssBaseline />
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <Typography variant="h1" className={styles.title}>
              Clients
            </Typography>
            <Typography variant="body1" className={styles.subtitle}>
              Manage your client database and their associated contacts
            </Typography>
          </div>
          <Button
              variant="contained"
            className={styles.addButton}
              onClick={() => showModal()}
            startIcon={<Building2 size={20} />}
            >
              Add New Client
          </Button>
        </div>

        <div className={styles.searchSection}>
          <TextField
            className={styles.searchBar}
            placeholder="Search clients..."
            onChange={(e) => debouncedSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 1 }}>
                  <Search size={18} />
                </Box>
              ),
            }}
            fullWidth
            variant="standard"
          />
        </div>

        {isLoading ? (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}>Loading...</div>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className={styles.emptyState}>
            <AlertCircle className={styles.emptyStateIcon} />
            <Typography className={styles.emptyStateText}>
              No clients found. Try adjusting your search or add a new client.
            </Typography>
            <Button
              variant="contained"
              className={styles.addButton}
              onClick={() => showModal()}
              startIcon={<Building2 size={20} />}
            >
              Add New Client
            </Button>
          </div>
        ) : isMobile ? (
          renderMobileView()
        ) : (
          <TableContainer className={styles.tableContainer}>
            <Table className={styles.table}>
              <TableHead className={styles.tableHead}>
                  {table.getHeaderGroups().map(headerGroup => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                      <TableCell key={header.id} className={styles.tableHeadCell}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {table.getRowModel().rows.map(row => (
                  <React.Fragment key={row.id}>
                    <TableRow 
                      className={`${styles.tableRow} ${expandedClientId === row.original.client_id ? styles.expandedRow : ''}`}
                      onClick={(e) => toggleExpand(row.original.client_id, e)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className={styles.tableCell}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    
                    {/* Expanded view */}
                    {expandedClientId === row.original.client_id && (
                      <TableRow className={styles.expandedContentRow}>
                        <TableCell colSpan={table.getAllColumns().length} className={styles.expandedContentCell}>
                          <div className={styles.expandedContentWrapper}>
                            <Tabs 
                              value={expandedViewTab} 
                              onChange={(e, newValue) => setExpandedViewTab(newValue)}
                              className={styles.expandedTabs}
                              TabIndicatorProps={{
                                style: {
                                  backgroundColor: '#034832',
                                  height: '3px'
                                }
                              }}
                            >
                              <Tab 
                                label="Contacts" 
                                className={styles.expandedMainTab}
                                disableRipple
                              />
                              <Tab 
                                label="Sites" 
                                className={styles.expandedMainTab}
                                disableRipple
                              />
                              <Tab 
                                label="Details" 
                                className={styles.expandedMainTab}
                                disableRipple
                              />
                            </Tabs>
                            
                            {/* Contacts Tab */}
                            {expandedViewTab === 0 && (
                              <div className={styles.expandedTabContent}>
                                <div className={styles.expandedTabHeader}>
                                  <Typography variant="h6" className={styles.tabTitle}>Contacts</Typography>
                                  <Button 
                                    variant="contained"
                                    className={styles.addContactButton}
                                    onClick={() => {
                                      setEditingClient(row.original);
                                      setEditingContact(blankContact());
                                      setIsContactFormOpen(true);
                                    }}
                                    startIcon={<UserPlus size={14} />}
                                  >
                                    Add Contact
                                  </Button>
                                </div>
                                
                                {clientContacts.length === 0 ? (
                                  <div className={styles.emptyContactsContainer}>
                                    <div className={styles.emptyContactsContent}>
                                      <AlertCircle size={36} className={styles.emptyIcon} />
                                      <Typography className={styles.emptyTitle}>No contacts found</Typography>
                                      <Typography className={styles.emptySubtitle}>Add a contact to help manage this client</Typography>
                                      <Button
                                        variant="contained"
                                        className={styles.addEmptyButton}
                                        onClick={() => {
                                          setEditingClient(row.original);
                                          setEditingContact(blankContact());
                                          setIsContactFormOpen(true);
                                        }}
                                        startIcon={<UserPlus size={14} />}
                                      >
                                        Add First Contact
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className={styles.contactsCardContainer}>
                                    {clientContacts.map((contact) => (
                                      <div key={contact.contact_id} className={styles.contactCard}>
                                        <div className={styles.contactCardTop}>
                                          {contact.is_primary && (
                                            <div className={styles.primaryTag}>
                                              <Star size={12} />
                                              <span>Primary</span>
                                            </div>
                                          )}
                                          <div className={styles.contactCardActions}>
                                            {!contact.is_primary && (
                                              <Tooltip title="Set as primary contact" arrow>
                                                <IconButton
                                                  className={styles.contactCardAction}
                                                  onClick={() => handleSetPrimaryContact(row.original.client_id, contact.contact_id)}
                                                  disabled={Boolean(primaryContactSubmittingId)}
                                                  aria-label={`Set ${contact.first_name} ${contact.last_name} as primary contact`}
                                                  size="small"
                                                >
                                                  <Star size={14} />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            <Tooltip title="Edit contact" arrow>
                                              <IconButton
                                                className={styles.contactCardAction}
                                                onClick={() => {
                                                  setEditingClient(row.original);
                                                  setEditingContact(contact);
                                                  setIsContactFormOpen(true);
                                                }}
                                                size="small"
                                              >
                                                <Edit size={14} />
                                              </IconButton>
                                            </Tooltip>
                                          </div>
                                        </div>
                                        
                                        <div className={styles.contactCardName}>
                                          {contact.first_name} {contact.last_name}
                                        </div>
                                        
                                        <div className={styles.contactCardInfo}>
                                          <div 
                                            className={styles.contactInfoItem}
                                            onClick={(e) => handleCopyToClipboard(contact.email, 'email', e)}
                                          >
                                            <Mail size={14} className={styles.contactInfoIcon} />
                                            <div className={styles.contactInfoText}>
                                              <div className={styles.contactInfoLabel}>Email</div>
                                              <div className={styles.contactInfoValue}>{contact.email}</div>
                                            </div>
                                            <div className={styles.copyIndicator}>
                                              <Copy size={12} />
                                            </div>
                                          </div>
                                          
                                          <div 
                                            className={styles.contactInfoItem}
                                            onClick={(e) => handleCopyToClipboard(contact.phone, 'phone', e)}
                                          >
                                            <Phone size={14} className={styles.contactInfoIcon} />
                                            <div className={styles.contactInfoText}>
                                              <div className={styles.contactInfoLabel}>Phone</div>
                                              <div className={styles.contactInfoValue}>{contact.phone}</div>
                                            </div>
                                            <div className={styles.copyIndicator}>
                                              <Copy size={12} />
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {contact.notes && (
                                          <div className={styles.contactCardNotes}>
                                            <div className={styles.contactNotesLabel}>Notes</div>
                                            <div className={styles.contactNotesContent}>{contact.notes}</div>
                                          </div>
                                        )}
                                        
                                        <div className={styles.contactCardFooter}>
                                          <Button
                                            variant="text"
                                            size="small"
                                            className={styles.siteAssignButton}
                                            onClick={() => handleOpenSiteAssignmentModal(contact)}
                                            startIcon={<Link2 size={14} />}
                                          >
                                            Manage site assignments
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Sites Tab */}
                            {expandedViewTab === 1 && (
                              <div className={styles.expandedTabContent}>
                                <div className={styles.expandedTabHeader}>
                                  <Typography variant="h6">Sites</Typography>
                                  <Button 
                                    variant="contained"
                                    className={styles.addContactButton}
                                    onClick={() => handleOpenSiteModal(row.original)}
                                    startIcon={<MapPin size={14} />}
                                  >
                                    Add Site
                                  </Button>
                                </div>
                                
                                <Tabs 
                                  value={contactTabValue} 
                                  onChange={handleContactTabChange}
                                  className={styles.siteSubTabs}
                                  TabIndicatorProps={{
                                    style: {
                                      backgroundColor: '#034832',
                                    }
                                  }}
                                >
                                  <Tab 
                                    label="Site List" 
                                    sx={{
                                      color: contactTabValue === 0 ? '#034832' : '#6B7280',
                                      fontWeight: contactTabValue === 0 ? 600 : 400,
                                      '&.Mui-selected': {
                                        color: '#034832',
                                      },
                                    }}
                                  />
                                  <Tab 
                                    label="Site Assignments" 
                                    sx={{
                                      color: contactTabValue === 1 ? '#034832' : '#6B7280',
                                      fontWeight: contactTabValue === 1 ? 600 : 400,
                                      '&.Mui-selected': {
                                        color: '#034832',
                                      },
                                    }}
                                  />
                                </Tabs>
                                
                                {contactTabValue === 0 ? (
                                  // Site list view - simplified without expandable accordion
                                  clientSites.length === 0 ? (
                                    <div className={styles.expandedEmptyState}>
                                      <Building size={48} className={styles.emptyStateIcon} />
                                      <Typography className={styles.emptyStateText}>
                                        No sites found for this client.
                                      </Typography>
                                    </div>
                                  ) : (
                                    <div className={styles.sitesCompactList}>
                                      {clientSites.map((site) => (
                                        <div key={site.site_id} className={styles.siteCompactItem}>
                                          <div className={styles.siteCompactHeader}>
                                            <Typography variant="subtitle1" className={styles.siteCompactName}>
                                              {site.site_name}
                                            </Typography>
                                            <Tooltip title="Edit Site">
                                              <IconButton
                                                aria-label={`Edit Site ${site.site_name}`}
                                                className={styles.siteCompactEditButton}
                                                size="small"
                                                onClick={() => handleOpenEditSiteModal(row.original, site)}
                                              >
                                                <Edit size={16} />
                                              </IconButton>
                                            </Tooltip>
                                          </div>
                                          <div className={styles.siteCompactDetails}>
                                            <div className={styles.siteCompactInfo}>
                                              <Globe size={16} className={styles.siteCompactIcon} />
                                              <Typography variant="body2" color="textSecondary">
                                                {site.region}
                                              </Typography>
                                            </div>
                                            <div className={styles.siteCompactInfo}>
                                              <MapPin size={16} className={styles.siteCompactIcon} />
                                              <Typography variant="body2" color="textSecondary">
                                                {site.address}
                                              </Typography>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )
                                ) : (
                                  // Site assignments matrix view - more compact
                                  <div className={styles.siteAssignmentsCompactContainer}>
                                    {clientSitesLoading ? (
                                      <div className={styles.loadingOverlay}>
                                        <div className={styles.loadingSpinner}>Loading sites...</div>
                                      </div>
                                    ) : clientSites.length === 0 ? (
                                      <div className={styles.emptyContacts}>
                                        <Building size={48} className={styles.emptyContactsIcon} />
                                        <Typography className={styles.emptyContactsText}>
                                          No sites found for this client.
                                        </Typography>
                                      </div>
                                    ) : (
                                      <div className={styles.siteContactMatrix}>
                                        <TableContainer className={styles.siteContactTable}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell className={`${styles.siteContactHeaderCell} ${styles.siteHeaderCell}`}>Site</TableCell>
                                                {clientContacts.map(contact => (
                                                  <TableCell 
                                                    key={contact.contact_id}
                                                    className={styles.siteContactHeaderCell}
                                                    align="center"
                                                  >
                                                    <div className={styles.contactHeaderCell}>
                                                      <div className={styles.contactHeaderName}>
                                                        {contact.first_name} {contact.last_name}
                                                      </div>
                                                      {contact.is_primary && (
                                                        <span className={styles.contactHeaderBadge}>
                                                          <Star size={12} />
                                                        </span>
                                                      )}
                                                    </div>
                                                  </TableCell>
                                                ))}
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {clientSites.map(site => (
                                                <TableRow key={site.site_id} className={styles.siteContactRow}>
                                                  <TableCell className={styles.siteCell}>
                                                    <div className={styles.siteCellContent}>
                                                      <div className={styles.siteName}>
                                                        {site.site_name}
                                                      </div>
                                                      <div className={styles.siteRegion}>
                                                        {site.region}
                                                      </div>
                                                    </div>
                                                  </TableCell>
                                                  {clientContacts.map(contact => (
                                                    <TableCell 
                                                      key={`${site.site_id}-${contact.contact_id}`}
                                                      align="center"
                                                      className={`${styles.contactAssignmentCell} ${siteContactMatrix[site.site_id]?.[contact.contact_id] ? styles.contactAssignmentCellActive : ''}`}
                                                      onClick={async () => {
                                                        const isCurrentlyAssigned = siteContactMatrix[site.site_id]?.[contact.contact_id] || false;
                                                        await toggleSiteAssignment(
                                                          site.site_id, 
                                                          contact.contact_id, 
                                                          !isCurrentlyAssigned
                                                        );
                                                      }}
                                                    >
                                                      <div className={styles.contactAssignmentCheckboxWrapper}>
                                                        <Checkbox
                                                          className={styles.contactAssignmentCheckbox}
                                                          checked={siteContactMatrix[site.site_id]?.[contact.contact_id] || false}
                                                          disabled={true}
                                                          size="small"
                                                        />
                                                      </div>
                                                    </TableCell>
                                                  ))}
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Details Tab */}
                            {expandedViewTab === 2 && (
                              <div className={styles.expandedTabContent}>
                                <div className={styles.expandedTabHeader}>
                                  <Typography variant="h6">Client Details</Typography>
                                  <Button 
                                    variant="outlined"
                                    className={styles.editButton}
                                    onClick={() => showModal(row.original)}
                                    startIcon={<Edit size={16} />}
                                  >
                                    Edit Client
                                  </Button>
                                </div>
                                
                                <div className={styles.clientDetailsGrid}>
                                  <div className={styles.clientDetailCard}>
                                    <div className={styles.clientDetailLabel}>Client Name</div>
                                    <div className={styles.clientDetailValue}>{row.original.client_name}</div>
                                  </div>
                                  {row.original.primary_contact && (
                                    <>
                                      <div className={styles.clientDetailCard}>
                                        <div className={styles.clientDetailLabel}>Primary Contact</div>
                                        <div className={styles.clientDetailValue}>
                                          {row.original.primary_contact.first_name} {row.original.primary_contact.last_name}
                                        </div>
                                      </div>
                                      <div className={styles.clientDetailCard}>
                                        <div className={styles.clientDetailLabel}>Email</div>
                                        <div className={styles.clientDetailValue}>{row.original.primary_contact.email}</div>
                                      </div>
                                      <div className={styles.clientDetailCard}>
                                        <div className={styles.clientDetailLabel}>Phone</div>
                                        <div className={styles.clientDetailValue}>{row.original.primary_contact.phone}</div>
                                      </div>
                                    </>
                                  )}
                                  <div className={styles.clientDetailCard}>
                                    <div className={styles.clientDetailLabel}>Total Contacts</div>
                                    <div className={styles.clientDetailValue}>{row.original.contact_count || 0}</div>
                                  </div>
                                  <div className={styles.clientDetailCard}>
                                    <div className={styles.clientDetailLabel}>Total Sites</div>
                                    <div className={styles.clientDetailValue}>{row.original.site_count || 0}</div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Client Modal */}
        <Dialog 
          open={isModalOpen} 
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          className={styles.modal}
        >
          <DialogTitle className={styles.modalTitle}>
            {isEditMode ? "Edit Client" : "Add New Client"}
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            <form onSubmit={handleSubmit}>
              <TextField
                className={styles.formField}
                label="Client Name"
                name="client_name"
                value={editingClient?.client_name || ''}
                onChange={(e) => setEditingClient({...editingClient, client_name: e.target.value})}
                required
                margin="normal"
              />
              <TextField
                className={styles.formField}
                label="First Name"
                name="contact_first_name"
                value={editingClient?.contact_first_name || ''}
                onChange={(e) => setEditingClient({...editingClient, contact_first_name: e.target.value})}
                required
                margin="normal"
              />
              <TextField
                className={styles.formField}
                label="Last Name"
                name="contact_last_name"
                value={editingClient?.contact_last_name || ''}
                onChange={(e) => setEditingClient({...editingClient, contact_last_name: e.target.value})}
                required
                margin="normal"
              />
              <TextField
                className={styles.formField}
                label="Phone"
                name="contact_phone"
                value={editingClient?.contact_phone || ''}
                onChange={(e) => setEditingClient({...editingClient, contact_phone: e.target.value})}
                required
                margin="normal"
              />
              <TextField
                className={styles.formField}
                label="Email"
                name="contact_email"
                type="email"
                value={editingClient?.contact_email || ''}
                onChange={(e) => setEditingClient({...editingClient, contact_email: e.target.value})}
                required
                margin="normal"
              />
            </form>
          </DialogContent>
          <DialogActions className={styles.modalActions}>
            <Button onClick={handleCloseModal} className={styles.cancelButton}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} className={styles.submitButton}>
              {isEditMode ? 'Update Client' : 'Add Client'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sites Modal */}
        <Dialog
          open={isSitesModalOpen}
          onClose={handleCloseSitesModal}
          maxWidth="md"
          fullWidth
          className={styles.sitesModal}
        >
          <DialogTitle className={styles.sitesModalHeader}>
            <Typography className={styles.sitesModalTitle}>Client Sites</Typography>
            <IconButton onClick={handleCloseSitesModal} size="small">
              <X size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              className={styles.sitesSearchBar}
              fullWidth
              placeholder="Search sites..."
              value={searchInputValue}
              onChange={handleSearchInputChange}
              InputProps={{
                startAdornment: <Search size={20} color="#666666" />,
              }}
            />
            <div className={styles.sitesList}>
              {filteredSites.map((site) => (
                <Accordion key={site.site_id} className={styles.siteAccordion}>
                  <AccordionSummary
                    expandIcon={<ChevronDown />}
                    className={styles.siteAccordionSummary}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                          {site.site_name}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="textSecondary">
                            Region: {site.region}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Address: {site.address}
                          </Typography>
                        </>
                      }
                    />
                  </AccordionSummary>
                  <AccordionDetails className={styles.siteAccordionDetails}>
                    <SiteContactsManager 
                      site={site}
                      clientId={editingClient?.client_id}
                      onContactsUpdate={() => {
                        if (!editingClient?.client_id) return;
                        invalidateClientCache(editingClient.client_id, ['matrix']);
                        fetchClientSites(editingClient.client_id);
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Contacts Modal */}
        <Dialog
          open={isContactModalOpen}
          onClose={() => {
            setIsContactModalOpen(false);
            setSearchInputValue('');
            setContactsFilter('all');
            setIsContactFormOpen(false);
            setContactTabValue(0);
          }}
          maxWidth="md"
          fullWidth
          className={styles.contactsModal}
        >
          <DialogTitle className={styles.contactsModalHeader}>
            <Typography className={styles.contactsModalTitle}>
              Manage Contacts
            </Typography>
            <Typography className={styles.contactsModalSubtitle}>
              <Building2 size={18} />
              {editingClient?.client_name} • {clientContacts.length} contact{clientContacts.length !== 1 ? 's' : ''}
            </Typography>
            <IconButton 
              onClick={() => setIsContactModalOpen(false)}
              sx={{ 
                position: 'absolute',
                right: 24,
                top: 24,
                color: '#6B7280',
              }}
            >
              <X size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Tabs 
              value={contactTabValue} 
              onChange={handleContactTabChange}
              className={styles.contactsTabs}
              TabIndicatorProps={{
                style: {
                  backgroundColor: '#034832',
                }
              }}
            >
              <Tab 
                label="All Contacts" 
                sx={{
                  color: contactTabValue === 0 ? '#034832' : '#6B7280',
                  fontWeight: contactTabValue === 0 ? 600 : 400,
                  '&.Mui-selected': {
                    color: '#034832',
                  },
                }}
              />
              <Tab 
                label="Site Assignments" 
                sx={{
                  color: contactTabValue === 1 ? '#034832' : '#6B7280',
                  fontWeight: contactTabValue === 1 ? 600 : 400,
                  '&.Mui-selected': {
                    color: '#034832',
                  },
                }}
              />
            </Tabs>

            {contactTabValue === 0 ? (
              <>
                <div className={styles.contactsToolbar}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Button
                      variant="contained"
                      className={styles.addButton}
                      onClick={() => {
                        setEditingContact(blankContact());
                        setIsContactFormOpen(true);
                      }}
                      startIcon={<UserPlus size={20} />}
                    >
                      Add Contact
                    </Button>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button
                        variant={contactsFilter === 'all' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setContactsFilter('all')}
                        sx={{
                          borderRadius: '8px',
                          color: contactsFilter === 'all' ? 'white' : '#6B7280',
                          backgroundColor: contactsFilter === 'all' ? '#034832' : 'transparent',
                          borderColor: contactsFilter === 'all' ? '#034832' : '#E5E7EB',
                          '&:hover': {
                            backgroundColor: contactsFilter === 'all' ? '#023324' : '#F9FAFB',
                            borderColor: contactsFilter === 'all' ? '#023324' : '#034832',
                          }
                        }}
                      >
                        All Contacts
                      </Button>
                      <Button
                        variant={contactsFilter === 'primary' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setContactsFilter('primary')}
                        startIcon={<Star size={16} />}
                        sx={{
                          borderRadius: '8px',
                          color: contactsFilter === 'primary' ? 'white' : '#6B7280',
                          backgroundColor: contactsFilter === 'primary' ? '#034832' : 'transparent',
                          borderColor: contactsFilter === 'primary' ? '#034832' : '#E5E7EB',
                          '&:hover': {
                            backgroundColor: contactsFilter === 'primary' ? '#023324' : '#F9FAFB',
                            borderColor: contactsFilter === 'primary' ? '#023324' : '#034832',
                          }
                        }}
                      >
                        Primary Only
                      </Button>
                    </div>
                  </div>
                  <div className={styles.contactsSearchBar}>
                    <Search size={20} />
                    <input
                      type="text"
                      placeholder="Search contacts by name, email, or phone..."
                      value={searchInputValue}
                      onChange={handleSearchInputChange}
                    />
                    {searchInputValue && (
                      <IconButton
                        size="small"
                        onClick={() => setSearchInputValue('')}
                        sx={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#9CA3AF',
                          '&:hover': { color: '#111827' }
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    )}
                  </div>
                </div>

                {clientContacts.length === 0 ? (
                  <div className={styles.emptyContacts}>
                    <AlertCircle size={48} className={styles.emptyContactsIcon} />
                    <Typography className={styles.emptyContactsText}>
                      No contacts found for this client. Add your first contact to get started.
                    </Typography>
                    <Button
                      variant="contained"
                      className={styles.addButton}
                      onClick={() => {
                        setEditingContact(blankContact());
                        setIsContactFormOpen(true);
                      }}
                      startIcon={<UserPlus size={20} />}
                    >
                      Add First Contact
                    </Button>
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className={styles.emptyContacts}>
                    <AlertCircle size={48} className={styles.emptyContactsIcon} />
                    <Typography className={styles.emptyContactsText}>
                      No contacts match your search criteria. Try adjusting your filters or search term.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSearchInputValue('');
                        setContactsFilter('all');
                      }}
                      startIcon={<X size={20} />}
                    >
                      Clear Filters
                    </Button>
                  </div>
                ) : (
                  <List className={styles.contactsList}>
                    {filteredContacts.map((contact) => (
                      <ListItem key={contact.contact_id} className={styles.contactItem}>
                        <div style={{ width: '100%' }}>
                          <div className={styles.contactHeader}>
                            <Typography className={styles.contactName}>
                              {contact.first_name} {contact.last_name}
                              {contact.is_primary && (
                                <span className={styles.primaryBadge}>
                                  <Star size={14} />
                                  Primary Contact
                                </span>
                              )}
                            </Typography>
                            <div className={styles.contactActions}>
                              {!contact.is_primary && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  className={styles.setPrimaryButton}
                                  onClick={() => handleSetPrimaryContact(editingClient.client_id, contact.contact_id)}
                                  disabled={Boolean(primaryContactSubmittingId)}
                                  startIcon={<Star size={16} />}
                                >
                                  Set as Primary
                                </Button>
                              )}
                              <Tooltip title="Assign to sites" arrow>
                                <IconButton
                                  onClick={() => handleOpenSiteAssignmentModal(contact)}
                                  size="small"
                                  sx={{ 
                                    color: '#6B7280',
                                    '&:hover': { 
                                      color: '#034832',
                                      backgroundColor: 'rgba(3, 72, 50, 0.04)'
                                    }
                                  }}
                                >
                                  <Link2 size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit contact" arrow>
                                <IconButton
                                  onClick={() => {
                                    setEditingContact(contact);
                                    setIsContactFormOpen(true);
                                  }}
                                  size="small"
                                  sx={{ 
                                    color: '#6B7280',
                                    '&:hover': { 
                                      color: '#034832',
                                      backgroundColor: 'rgba(3, 72, 50, 0.04)'
                                    }
                                  }}
                                >
                                  <Edit size={16} />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                          <div className={styles.contactInfo}>
                            <Tooltip title="Click to copy email" arrow>
                              <div 
                                className={styles.contactDetail}
                                onClick={(e) => handleCopyToClipboard(contact.email, 'email', e)}
                              >
                                <Mail size={16} />
                                {contact.email}
                              </div>
                            </Tooltip>
                            <Tooltip title="Click to copy phone" arrow>
                              <div 
                                className={styles.contactDetail}
                                onClick={(e) => handleCopyToClipboard(contact.phone, 'phone', e)}
                              >
                                <Phone size={16} />
                                {contact.phone}
                              </div>
                            </Tooltip>
                            {contact.notes && (
                              <div className={styles.contactNotes}>
                                {contact.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            ) : (
              <div className={styles.siteAssignmentsContainer}>
                <div className={styles.siteAssignmentsHeader}>
                  <Typography className={styles.siteAssignmentsTitle}>
                    Site Contact Assignments
                  </Typography>
                  <Typography className={styles.siteAssignmentsSubtitle}>
                    Manage which contacts are assigned to each site
                  </Typography>
                </div>
                
                {clientSitesLoading ? (
                  <div className={styles.loadingOverlay}>
                    <div className={styles.loadingSpinner}>Loading sites...</div>
                  </div>
                ) : clientSites.length === 0 ? (
                  <div className={styles.emptyContacts}>
                    <Building size={48} className={styles.emptyContactsIcon} />
                    <Typography className={styles.emptyContactsText}>
                      No sites found for this client. You need to add sites before you can assign contacts.
                    </Typography>
                  </div>
                ) : (
                  <div className={styles.siteContactMatrix}>
                    <TableContainer className={styles.siteContactTable}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell className={`${styles.siteContactHeaderCell} ${styles.siteHeaderCell}`}>Site</TableCell>
                            {clientContacts.map(contact => (
                              <TableCell 
                                key={contact.contact_id}
                                className={styles.siteContactHeaderCell}
                                align="center"
                              >
                                <div className={styles.contactHeaderCell}>
                                  <div className={styles.contactHeaderName}>
                                    {contact.first_name} {contact.last_name}
                                  </div>
                                  {contact.is_primary && (
                                    <span className={styles.contactHeaderBadge}>
                                      <Star size={12} />
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clientSites.map(site => (
                            <TableRow key={site.site_id} className={styles.siteContactRow}>
                              <TableCell className={styles.siteCell}>
                                <div className={styles.siteCellContent}>
                                  <div className={styles.siteName}>
                                    {site.site_name}
                                  </div>
                                  <div className={styles.siteRegion}>
                                    {site.region}
                                  </div>
                                </div>
                              </TableCell>
                              {clientContacts.map(contact => (
                                <TableCell 
                                  key={`${site.site_id}-${contact.contact_id}`}
                                  align="center"
                                  className={`${styles.contactAssignmentCell} ${siteContactMatrix[site.site_id]?.[contact.contact_id] ? styles.contactAssignmentCellActive : ''}`}
                                  onClick={async () => {
                                    const isCurrentlyAssigned = siteContactMatrix[site.site_id]?.[contact.contact_id] || false;
                                    await toggleSiteAssignment(
                                      site.site_id, 
                                      contact.contact_id, 
                                      !isCurrentlyAssigned
                                    );
                                  }}
                                >
                                  <div className={styles.contactAssignmentCheckboxWrapper}>
                                    <Checkbox
                                      className={styles.contactAssignmentCheckbox}
                                      checked={siteContactMatrix[site.site_id]?.[contact.contact_id] || false}
                                      disabled={true}
                                      size="small"
                                    />
                                  </div>
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Contact Form Dialog */}
        <Dialog
          open={isContactFormOpen}
          onClose={() => setIsContactFormOpen(false)}
          maxWidth="sm"
          fullWidth
          className={styles.modal}
        >
          <DialogTitle className={styles.modalTitle}>
            {editingContact?.contact_id ? "Edit Contact" : "Add New Contact"}
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            <form onSubmit={handleContactSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    className={styles.formField}
                    label="First Name"
                    value={editingContact?.first_name || ''}
                    onChange={(e) => setEditingContact({...editingContact, first_name: e.target.value})}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    className={styles.formField}
                    label="Last Name"
                    value={editingContact?.last_name || ''}
                    onChange={(e) => setEditingContact({...editingContact, last_name: e.target.value})}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    className={styles.formField}
                    label="Email"
                    type="email"
                    value={editingContact?.email || ''}
                    onChange={(e) => setEditingContact({...editingContact, email: e.target.value})}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    className={styles.formField}
                    label="Phone"
                    value={editingContact?.phone || ''}
                    onChange={(e) => setEditingContact({...editingContact, phone: e.target.value})}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    className={styles.formField}
                    label="Notes"
                    multiline
                    rows={4}
                    value={editingContact?.notes || ''}
                    onChange={(e) => setEditingContact({...editingContact, notes: e.target.value})}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions className={styles.modalActions}>
            <Button
              onClick={() => setIsContactFormOpen(false)}
              className={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={handleContactSubmit}
              className={styles.submitButton}
              disabled={isContactSubmitting}
            >
              {editingContact?.contact_id ? 'Update Contact' : 'Add Contact'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Site Assignment Modal */}
        <Dialog
          open={isSiteAssignmentModalOpen}
          onClose={handleCloseSiteAssignmentModal}
          maxWidth="sm"
          fullWidth
          className={styles.siteAssignmentModal}
        >
          <DialogTitle className={styles.modalTitle}>
            <Typography component="div" className={styles.siteAssignmentModalTitle}>
              Assign Contact to Sites
            </Typography>
            <Typography component="div" className={styles.siteAssignmentModalSubtitle}>
              <span className={styles.siteAssignmentContactName}>
                {selectedContactForSites?.first_name} {selectedContactForSites?.last_name}
              </span>
              {selectedContactForSites?.is_primary && (
                <span className={styles.siteAssignmentPrimaryBadge}>
                  <Star size={14} />
                  Primary Contact
                </span>
              )}
            </Typography>
            <IconButton 
              onClick={handleCloseSiteAssignmentModal}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: '#6B7280'
              }}
            >
              <X size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent className={styles.siteAssignmentModalContent}>
            {clientSitesLoading ? (
              <div className={styles.loadingOverlay}>
                <div className={styles.loadingSpinner}>Loading sites...</div>
              </div>
            ) : clientSites.length === 0 ? (
              <div className={styles.emptyContacts}>
                <Building size={48} className={styles.emptyContactsIcon} />
                <Typography className={styles.emptyContactsText}>
                  No sites found for this client. You need to add sites before you can assign contacts.
                </Typography>
              </div>
            ) : (
              <>
                <div className={styles.siteAssignmentHeader}>
                  <Typography className={styles.siteAssignmentInstructions}>
                    <CheckCircle size={16} className={styles.instructionIcon} />
                    Select the sites where this contact should be assigned
                  </Typography>
                </div>
                <List className={styles.siteAssignmentList}>
                  {clientSites.map((site) => (
                    <ListItem key={site.site_id} className={styles.siteAssignmentItem}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={!!siteContactAssignments[site.site_id]}
                            onChange={(e) => {
                              if (selectedContactForSites) {
                                toggleSiteAssignment(
                                  site.site_id,
                                  selectedContactForSites.contact_id,
                                  e.target.checked
                                );
                              }
                            }}
                            className={styles.siteAssignmentCheckbox}
                          />
                        }
                        label={
                          <div className={styles.siteAssignmentLabel}>
                            <div className={styles.siteAssignmentName}>
                              {site.site_name}
                            </div>
                            <div className={styles.siteAssignmentRegion}>
                              <Globe size={14} className={styles.siteAssignmentIcon} />
                              {site.region}
                            </div>
                            <div className={styles.siteAssignmentAddress}>
                              <MapPin size={14} className={styles.siteAssignmentIcon} />
                              {site.address}
                            </div>
                          </div>
                        }
                        className={styles.siteAssignmentFormControl}
                      />
                    </ListItem>
                  ))}
                </List>
                <div className={styles.siteAssignmentSummary}>
                  <div className={styles.siteAssignmentCount}>
                    {Object.values(siteContactAssignments).filter(Boolean).length} of {clientSites.length} sites selected
                  </div>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions className={styles.modalActions}>
            <Button
              onClick={handleCloseSiteAssignmentModal}
              className={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloseSiteAssignmentModal}
              className={styles.submitButton}
              endIcon={<CheckCircle size={16} />}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Popover */}
        <Popover
          open={Boolean(deletePopoverId)}
          anchorEl={deletePopoverId}
          onClose={() => setDeletePopoverId(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box p={2}>
            <Typography variant="body1" style={{ marginBottom: 16 }}>
              Are you sure you want to delete this client?
            </Typography>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                onClick={() => setDeletePopoverId(null)}
                className={styles.cancelButton}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDelete(editingClient?.client_id)}
                className={styles.submitButton}
                color="error"
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Popover>

        {copiedTooltip.show && (
          <div 
            className={styles.copiedTooltip}
            style={{
              position: 'fixed',
              left: copiedTooltip.x,
              top: copiedTooltip.y,
              transform: 'translate(-50%, -100%)'
            }}
          >
            {copiedTooltip.text}
          </div>
        )}

        {/* Site Form Modal - Clean Design */}
        <Dialog
          open={isSiteModalOpen}
          onClose={handleCloseSiteModal}
          maxWidth="md"
          fullWidth
          className={styles.modal}
        >
          <DialogTitle className={styles.modalTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Building2 size={20} style={{ color: '#034832' }} />
              {siteDialogMode === 'edit'
                ? `Edit Site for ${editingClient?.client_name || ''}`
                : `Add New Site for ${editingClient?.client_name || ''}`}
            </div>
            <IconButton 
              onClick={handleCloseSiteModal}
              aria-label="Close site dialog"
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: '#6B7280',
              }}
            >
              <X size={18} />
            </IconButton>
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            {siteEditLoading ? (
              <Typography sx={{ py: 4, textAlign: 'center' }}>Loading site details...</Typography>
            ) : (
            <form
              onSubmit={siteDialogMode === 'edit' ? handleEditSiteSubmit : handleSiteSubmit}
              className={styles.siteForm}
            >
              <div className={styles.formSection} style={{ border: 'none', borderBottom: 'none', borderTop: 'none', paddingBottom: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  className={styles.sectionTitle}
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '15px',
                    '&::after': { display: 'none !important' },
                    '&::before': { display: 'none !important' }
                  }}
                >
                  <Building2 size={16} style={{ color: '#034832' }} />
                  Site Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Site Name"
                      value={siteForm?.site_name || ''}
                      onChange={(e) => setSiteForm({...siteForm, site_name: e.target.value})}
                      required
                      fullWidth
                      size="small"
                      placeholder="Enter site name"
                    />
                  </Grid>
                  {siteDialogMode === 'edit' && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        className={styles.formField}
                        label="Client"
                        value={editingClient?.client_name || ''}
                        disabled
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Region"
                      value={siteForm?.region || ''}
                      onChange={(e) => setSiteForm({...siteForm, region: e.target.value})}
                      required
                      fullWidth
                      size="small"
                      placeholder="Enter region"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div className={styles.formField}>
                      <PlacesAutocomplete
                        value={siteForm?.address || ''}
                        onChange={handleSiteAddressChange}
                        onSelect={handleAddressSelect}
                      >
                        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                          <div>
                            <TextField
                              {...getInputProps()}
                              placeholder="Search for address"
                              label="Address"
                              variant="outlined"
                              fullWidth
                              required
                              size="small"
                              InputProps={{
                                startAdornment: <InputAdornment position="start"><MapPin size={16} /></InputAdornment>,
                              }}
                            />
                            <div className={styles.autocompleteDropdown}>
                              {loading && <div style={{padding: '8px 12px', color: '#6B7280'}}>Loading...</div>}
                              {suggestions.map((suggestion) => {
                                const style = {
                                  backgroundColor: suggestion.active ? '#f5f5f5' : '#ffffff',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #e0e0e0',
                                  fontSize: '14px'
                                };
                                return (
                                  <div
                                    {...getSuggestionItemProps(suggestion, { style })}
                                    key={suggestion.placeId}
                                  >
                                    <span>{suggestion.description}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </PlacesAutocomplete>
                    </div>
                  </Grid>
                </Grid>
              </div>

              <div className={styles.formSection} style={{ border: 'none', borderBottom: 'none', borderTop: 'none', paddingBottom: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  className={styles.sectionTitle}
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '15px',
                    '&::after': { display: 'none !important' },
                    '&::before': { display: 'none !important' }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#034832" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3v18h18"></path>
                    <path d="M22 13H9"></path>
                    <path d="M22 8H7"></path>
                    <path d="M22 18H11"></path>
                  </svg>
                  Site Measurements
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Parking Lots Sqft"
                      type="number"
                      value={siteForm?.parking_lots_sqft ?? ''}
                      onChange={(e) => setSiteForm({...siteForm, parking_lots_sqft: validateNumber(e.target.value)})}
                      required
                      disabled={siteDialogMode === 'edit' && isClientSiteAllocationPresent(editingSite)}
                      title={siteDialogMode === 'edit' && isClientSiteAllocationPresent(editingSite) ? 'Ensure site deallocations before editing' : ''}
                      fullWidth
                      size="small"
                      placeholder="0"
                      InputProps={{ 
                        inputProps: { min: 0 },
                        endAdornment: <InputAdornment position="end">sqft</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Walkway Sqft"
                      type="number"
                      value={siteForm?.walkway_sqft ?? ''}
                      onChange={(e) => setSiteForm({...siteForm, walkway_sqft: validateNumber(e.target.value)})}
                      required
                      disabled={siteDialogMode === 'edit' && isClientSiteAllocationPresent(editingSite)}
                      title={siteDialogMode === 'edit' && isClientSiteAllocationPresent(editingSite) ? 'Ensure site deallocations before editing' : ''}
                      fullWidth
                      size="small"
                      placeholder="0"
                      InputProps={{ 
                        inputProps: { min: 0 },
                        endAdornment: <InputAdornment position="end">sqft</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="site_coverage"
                      name="site_coverage"
                      className={styles.formField}
                      label="Site Coverage (%)"
                      type="number"
                      value={siteForm?.site_coverage ?? 0}
                      onChange={(e) => setSiteForm({...siteForm, site_coverage: e.target.value})}
                      required
                      fullWidth
                      size="small"
                      InputProps={{
                        inputProps: { min: 0, max: 100, step: 1 },
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              </div>

              <div className={styles.formSection} style={{ border: 'none', borderBottom: 'none', borderTop: 'none', paddingBottom: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  className={styles.sectionTitle}
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '15px',
                    '&::after': { display: 'none !important' },
                    '&::before': { display: 'none !important' }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#034832" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                    <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                    <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                  </svg>
                  Contract Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth className={styles.formField} size="small">
                      <InputLabel id="contract-status-label">Contract Status</InputLabel>
                      <Select
                        labelId="contract-status-label"
                        label="Contract Status"
                        value={siteForm?.contract_status || ''}
                        onChange={(e) => setSiteForm({...siteForm, contract_status: e.target.value})}
                        required
                      >
                        <MenuItem value="Estimate">Estimate</MenuItem>
                        <MenuItem value="Unconfirmed">Unconfirmed</MenuItem>
                        <MenuItem value="Confirmed">Confirmed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Base Revenue"
                      type="number"
                      value={siteForm?.base_revenue ?? ''}
                      onChange={(e) => setSiteForm({...siteForm, base_revenue: validateNumber(e.target.value)})}
                      fullWidth
                      required={siteDialogMode === 'edit'}
                      size="small"
                      placeholder="0.00"
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: "0.01" }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>Winter Start Date</label>
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={siteForm?.winter_start_date ? moment(siteForm.winter_start_date).format('YYYY-MM-DD') : ''}
                        onChange={(e) => setSiteForm({...siteForm, winter_start_date: e.target.value})}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          className: styles.datePickerCompact
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>Winter End Date</label>
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={siteForm?.winter_end_date ? moment(siteForm.winter_end_date).format('YYYY-MM-DD') : ''}
                        onChange={(e) => setSiteForm({...siteForm, winter_end_date: e.target.value})}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          className: styles.datePickerCompact
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>Contract Expiration Date</label>
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={siteForm?.contract_expiration_date ? moment(siteForm.contract_expiration_date).format('YYYY-MM-DD') : ''}
                        onChange={(e) => setSiteForm({...siteForm, contract_expiration_date: e.target.value})}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          className: styles.datePickerCompact
                        }}
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>

              <div className={styles.formSection} style={{ border: 'none', borderBottom: 'none', borderTop: 'none', paddingBottom: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  className={styles.sectionTitle}
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '15px',
                    '&::after': { display: 'none !important' },
                    '&::before': { display: 'none !important' }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#034832" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path>
                    <path d="M12 18V6"></path>
                  </svg>
                  Additional Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Monthly Rate (No HST)"
                      type="number"
                      value={siteForm?.monthly_rate ?? ''}
                      onChange={(e) => setSiteForm({...siteForm, monthly_rate: validateNumber(e.target.value)})}
                      fullWidth
                      required={siteDialogMode === 'edit'}
                      size="small"
                      placeholder="0.00"
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: "0.01" }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth className={styles.formField} size="small">
                      <InputLabel id="territory-manager-label">Territory Manager</InputLabel>
                      <Select
                        labelId="territory-manager-label"
                        value={siteForm?.assigned_territory_manager || ''}
                        onChange={(e) => setSiteForm({...siteForm, assigned_territory_manager: e.target.value})}
                        label="Territory Manager"
                      >
                        <MenuItem value="">
                          <em>Select Territory Manager</em>
                        </MenuItem>
                        {territoryManagers.map((manager) => (
                          <MenuItem key={manager.name} value={manager.name}>
                            {manager.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {siteDialogMode === 'edit' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth className={styles.formField} size="small">
                          <InputLabel id="site-supervisor-label">Site Supervisor</InputLabel>
                          <Select
                            labelId="site-supervisor-label"
                            value={siteForm?.assigned_site_supervisor || ''}
                            onChange={(e) => setSiteForm({...siteForm, assigned_site_supervisor: e.target.value})}
                            label="Site Supervisor"
                          >
                            <MenuItem value=""><em>Select Site Supervisor</em></MenuItem>
                            {staffList.map((staff) => (
                              <MenuItem key={staff.staff_id || staff.id || staff.name} value={staff.name}>
                                {staff.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          className={styles.formField}
                          label="Viaesys Site ID"
                          value={siteForm?.viaesys_site_id || ''}
                          onChange={(e) => setSiteForm({...siteForm, viaesys_site_id: e.target.value})}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          className={styles.formField}
                          label="QBO Key Account ID"
                          value={siteForm?.qbo_key_account_id || ''}
                          onChange={(e) => setSiteForm({...siteForm, qbo_key_account_id: e.target.value})}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth className={styles.formField} size="small">
                          <InputLabel id="weather-station-label">Weather Station</InputLabel>
                          <Select
                            labelId="weather-station-label"
                            value={siteForm?.weather_station_id ?? ''}
                            onChange={(e) => setSiteForm({...siteForm, weather_station_id: e.target.value})}
                            label="Weather Station"
                          >
                            <MenuItem value=""><em>Select Weather Station</em></MenuItem>
                            {weatherStations.map((station) => (
                              <MenuItem key={station.id} value={station.id}>
                                {station.city_name}, {station.postal_code}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Additional Revenue Type"
                      value={siteForm?.additional_revenue_type || ''}
                      onChange={(e) => setSiteForm({...siteForm, additional_revenue_type: e.target.value})}
                      fullWidth
                      size="small"
                      placeholder="Enter revenue type"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Additional Revenue Amount"
                      type="number"
                      value={siteForm?.additional_revenue_amount ?? ''}
                      onChange={(e) => setSiteForm({...siteForm, additional_revenue_amount: validateNumber(e.target.value)})}
                      fullWidth
                      required={siteDialogMode === 'edit'}
                      size="small"
                      placeholder="0.00"
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: "0.01" }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      className={styles.formField}
                      label="Dispatch Notes"
                      multiline
                      rows={2}
                      value={siteForm?.dispatch_notes || ''}
                      onChange={(e) => setSiteForm({...siteForm, dispatch_notes: e.target.value})}
                      fullWidth
                      size="small"
                      placeholder="Enter any additional notes for dispatch"
                    />
                  </Grid>
                </Grid>
              </div>
              
              <input type="hidden" name="latitude" value={siteForm?.latitude || ''} />
              <input type="hidden" name="longitude" value={siteForm?.longitude || ''} />
            </form>
            )}
          </DialogContent>
          <DialogActions className={styles.modalActions}>
            <Button 
              onClick={handleCloseSiteModal}
              className={styles.cancelButton}
              disabled={siteSubmitting}
            >
              CANCEL
            </Button>
            <Button 
              onClick={siteDialogMode === 'edit' ? handleEditSiteSubmit : handleSiteSubmit}
              className={styles.submitButton}
              disabled={siteEditLoading || siteSubmitting}
            >
              {siteSubmitting ? 'SAVING...' : siteDialogMode === 'edit' ? 'UPDATE SITE' : 'ADD SITE'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ConfigProvider>
  );
};

export default ClientListView;
